import {
  tastingSuggestionResultSchema,
  type IngredientEvidence,
  type RecipeWriteInput,
  type TastingSuggestion,
} from '@recipes/contracts';
import { draftToRecipeInput } from '@recipes/domain';
import { Alert, Badge, Button, Card, CardContent, Spinner } from '@recipes/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { ProcessingIndicator } from '@/features/shared/processing-indicator';
import { toast } from '@/components/feedback/toaster';
import { FeedbackButtons } from '@/features/ai/feedback-buttons';
import { useGeneration } from '@/features/ai/use-generation';
import { DraftReviewForm } from '@/features/imports/draft-review-form';
import { WarningsList } from '@/features/imports/review-parts';
import { useProfile } from '@/features/shared/hooks';
import { DishPhotoChoice } from '@/features/tasting/dish-photo-choice';
import { FeaturePageHeader, PageContainer } from '@/features/shared/page-header';
import { api } from '@/lib/api';
import { describeError, describeWorkerError, toastError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';

const EVIDENCE_VARIANT: Record<IngredientEvidence, 'success' | 'info' | 'secondary' | 'muted'> = {
  OBSERVED: 'success',
  VISIBLE: 'info',
  MENU: 'info',
  SOURCE: 'secondary',
  TYPICAL: 'muted',
};

function SuggestionCard({
  suggestion,
  index,
  onReview,
}: {
  suggestion: TastingSuggestion;
  index: number;
  onReview: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const recipeSources = suggestion.sources.filter((s) => s.kind === 'RECIPE');
  return (
    <Card data-testid="tasting-suggestion">
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <h2 className="text-lg font-semibold">
            {index + 1}. {suggestion.title}
          </h2>
          {/* Every suggestion approximates the dish; it is never the restaurant's recipe. */}
          <Badge variant="warning">{t('tasting.suggest.approximation')}</Badge>
        </div>
        <p className="text-sm">{suggestion.rationale}</p>

        <div>
          <h3 className="text-sm font-medium">{t('tasting.suggest.ingredients')}</h3>
          <ul className="mt-1 flex flex-wrap gap-1.5">
            {suggestion.evidence.map((e) => (
              <li key={e.ingredient}>
                <Badge variant={EVIDENCE_VARIANT[e.evidence]} className="gap-1 font-normal">
                  {e.ingredient}
                  <span className="text-[10px] font-semibold uppercase opacity-80">
                    {t(`tasting.suggest.evidence.${e.evidence}`)}
                  </span>
                </Badge>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="text-sm font-medium">{t('tasting.suggest.sources')}</h3>
          {suggestion.sources.length ? (
            <ul className="mt-1 space-y-1 text-sm">
              {suggestion.sources.map((s) => (
                <li key={s.url} className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline">{t(`tasting.suggest.sourceKind.${s.kind}`)}</Badge>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center gap-1 text-primary underline"
                  >
                    {s.title} <ExternalLink className="size-3" aria-hidden />
                  </a>
                  <span className="text-xs text-muted-foreground">{s.domain}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-1 text-sm text-muted-foreground">{t('tasting.suggest.unsourced')}</p>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={onReview} data-testid="suggestion-review">
            {t('tasting.suggest.review')}
          </Button>
          {recipeSources[0] && (
            <Button
              variant="outline"
              onClick={() =>
                navigate(`/add/import?url=${encodeURIComponent(recipeSources[0]!.url)}`)
              }
            >
              {t('tasting.suggest.importSource')}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/** AI recipe suggestions for one dish: evidence-labelled approximations with sources. */
export function TastingSuggestionsPage() {
  const { noteId = '', generationId = '' } = useParams();
  const [params] = useSearchParams();
  const visitId = params.get('visit');
  const backTo = visitId ? `/tasting/visits/${visitId}` : '/tasting';
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const gen = useGeneration(generationId || null);
  const [reviewing, setReviewing] = useState<number | null>(null);

  // The photos of the dish: one of them becomes the picture of the recipe, the first by default.
  const visit = useQuery({
    queryKey: queryKeys.tasting.visit(visitId ?? ''),
    queryFn: () => api.visits.get(visitId!),
    enabled: Boolean(visitId),
  });
  const photos = useMemo(
    () =>
      (visit.data?.notes.find((note) => note.id === noteId)?.photos ?? []).filter(
        (photo) => photo.status !== 'DELETED' && photo.status !== 'FAILED',
      ),
    [visit.data, noteId],
  );
  const [pictureChoice, setPictureChoice] = useState<string | null | undefined>(undefined);
  const firstPhotoId = photos[0]?.assetId ?? null;
  useEffect(() => {
    setPictureChoice((current) => (current === undefined && firstPhotoId ? firstPhotoId : current));
  }, [firstPhotoId]);
  const picture = pictureChoice ?? null;

  const result = useMemo(() => {
    if (gen.generation?.status !== 'SUCCEEDED') return null;
    const parsed = tastingSuggestionResultSchema.safeParse(gen.generation.result);
    return parsed.success ? parsed.data : null;
  }, [gen.generation]);

  const save = useMutation({
    mutationFn: async (input: RecipeWriteInput) => {
      const recipe = await api.recipes.create(input, crypto.randomUUID());
      // The link is what makes the recipe show up as "inspired by" on both sides.
      await api.tastingNotes.linkRecipe(noteId, {
        recipeId: recipe.id,
        aiGenerationId: generationId,
        // A picture chosen in the form wins: the server only fills an empty place.
        coverFromPhotoAssetId: picture,
      });
      return recipe;
    },
    onSuccess: (recipe) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.recipes.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.tasting.all });
      toast.success(t('ai.review.saved'));
      navigate(`/recipes/${recipe.id}`);
    },
    onError: (e) => toastError(e, t('ai.review.saveFailed')),
  });

  if (gen.isLoading)
    return (
      <PageContainer>
        <Spinner label={t('app.loading')} />
      </PageContainer>
    );
  if (gen.error || !gen.generation)
    return (
      <PageContainer className="max-w-3xl">
        <FeaturePageHeader title={t('tasting.suggest.title')} backTo={backTo} />
        <Alert variant="destructive">{describeError(gen.error)}</Alert>
      </PageContainer>
    );
  const generation = gen.generation;

  if (generation.status === 'QUEUED' || generation.status === 'RUNNING')
    return (
      <PageContainer className="max-w-3xl">
        <FeaturePageHeader title={t('tasting.suggest.working')} backTo={backTo} />
        <ProcessingIndicator
          label={
            gen.progress < 25
              ? t('tasting.suggest.stepUnderstand')
              : gen.progress < 60
                ? t('tasting.suggest.stepContext')
                : t('tasting.suggest.stepReconcile')
          }
          progress={generation.status === 'RUNNING' ? gen.progress : null}
        />
      </PageContainer>
    );

  if (generation.status !== 'SUCCEEDED' || !result)
    return (
      <PageContainer className="max-w-3xl">
        <FeaturePageHeader title={t('tasting.suggest.title')} backTo={backTo} />
        <Alert
          variant="destructive"
          title={
            generation.status === 'SUCCEEDED'
              ? t('ai.unexpectedResult')
              : t(`ai.status.${generation.status}`)
          }
        >
          <p>
            {describeWorkerError(
              gen.streamError?.code ?? generation.errorCode,
              gen.streamError?.message ?? generation.error,
            )}
          </p>
          <Button asChild size="sm" variant="outline" className="mt-3">
            <Link to={backTo}>{t('common.back')}</Link>
          </Button>
        </Alert>
      </PageContainer>
    );

  if (reviewing !== null && result.suggestions[reviewing]) {
    const suggestion = result.suggestions[reviewing]!;
    const initial = draftToRecipeInput(suggestion.draft, {
      defaultServings: profile ? String(profile.defaultServings) : undefined,
    });
    return (
      <PageContainer className="max-w-3xl">
        <FeaturePageHeader
          title={t('ai.review.title')}
          description={t('tasting.suggest.reviewHint')}
          actions={
            <Button variant="outline" onClick={() => setReviewing(null)}>
              {t('tasting.suggest.backToList')}
            </Button>
          }
        />
        <Alert variant="warning" title={t('tasting.suggest.approximation')} className="mb-4">
          {t('tasting.suggest.approximationBody')}
        </Alert>
        <WarningsList warnings={suggestion.draft.warnings} />
        <div className="mb-4">
          <DishPhotoChoice photos={photos} value={picture} onChange={setPictureChoice} />
        </div>
        <DraftReviewForm
          key={reviewing}
          initial={initial}
          draft={suggestion.draft}
          origin="AI"
          submitLabel={t('ai.review.save')}
          submitting={save.isPending}
          onSubmit={(input) => save.mutate(input)}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer className="max-w-3xl">
      <FeaturePageHeader
        title={t('tasting.suggest.title')}
        description={t('tasting.suggest.description')}
        backTo={backTo}
        actions={
          <span className="text-xs text-muted-foreground">
            {generation.provider &&
              `${generation.provider}${generation.model ? ` · ${generation.model}` : ''}`}
          </span>
        }
      />
      <div className="space-y-4">
        {result.providerNote && (
          <Alert variant="info" data-testid="suggest-provider-note">
            {result.providerNote}
          </Alert>
        )}
        {result.dishCandidates.length > 0 && (
          <p className="text-sm">
            <span className="font-medium">{t('tasting.suggest.candidates')}: </span>
            {result.dishCandidates
              .map((c) => `${c.name} (${Math.round(c.confidence * 100)} %)`)
              .join(' · ')}
          </p>
        )}
        {result.menuMatch && (
          <p className="text-sm">
            <span className="font-medium">{t('tasting.suggest.menuMatch')}: </span>
            <a
              href={result.menuMatch.url}
              target="_blank"
              rel="noreferrer noopener"
              className="text-primary underline"
            >
              {result.menuMatch.name}
            </a>
            {result.menuMatch.description && ` – ${result.menuMatch.description}`}
          </p>
        )}
        <p className="text-xs text-muted-foreground">{t('tasting.suggest.legend')}</p>
        {result.suggestions.map((suggestion, index) => (
          <SuggestionCard
            key={index}
            suggestion={suggestion}
            index={index}
            onReview={() => setReviewing(index)}
          />
        ))}
        <FeedbackButtons generationId={generationId} initial={generation.feedback} />
      </div>
    </PageContainer>
  );
}
